import { useChatActions, useChatId, useChatStatus } from '@ai-sdk-tools/store';
import { useApolloClient } from '@apollo/client';
import { AppTypeEnum } from '@newfront-insurance/ai-api';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 } from 'uuid';

import type { ChatInputContextValue } from './chat-input-context';
import { getOptimisticChatAccountName } from './optimistic-chat';
import type { PromptEditorHandle } from './prompt-editor';
import { useScrollToBottom } from '../../../../hooks/use-scroll-to-bottom';
import { contextUploads } from '../../../../lib/chat-workspace';
import type { UIMessageWithMetadata } from '../../../../lib/types';
import { GET_CHAT_WITH_MESSAGES, GET_CHATS_BY_FILTERS } from '../../../../queries/chats';
import { useChatOverlayContext, type QueuedNewChatInput } from '../../contexts/chat-overlay-context';
import { useSkillsSafe } from '../../contexts/skills-context';
import { useNovaContext } from '../../hooks/use-chat-context';
import { getCountBucket, getLengthBucket, NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../../lib/analytics';
import { ACTIVE_CHAT_STATUS, buildOptimisticChat } from '../../lib/chat-optimistic-updates';
import { normalizeChatUuid } from '../../lib/notification-helpers';
import { buildNovaRequestContext } from '../../lib/request-context';
import { useNovaNavigationSafe } from '../../navigation/nova-navigation-context';

/**
 * Hook that sets up all chat input logic and returns the context value.
 * This hook focuses on core input functionality (text input, submit).
 * Toolbar components (like ChatAttachments) contribute additional context
 * via ChatOverlayContext.additionalContext.
 *
 * @example
 * ```tsx
 * function MyCustomInput() {
 *   const contextValue = useChatInputSetup();
 *
 *   return (
 *     <ChatInputProvider value={contextValue}>
 *       <ChatInput>
 *         <ChatInputTextarea />
 *         <ChatInputToolbar>
 *           <ChatAttachments /> // Sets additionalContext via ChatOverlayContext
 *           <SendButton />
 *         </ChatInputToolbar>
 *       </ChatInput>
 *     </ChatInputProvider>
 *   );
 * }
 * ```
 */
interface UseChatInputSetupOptions {
  /** Maximum allowed message length in characters. Undefined means no limit. */
  maxLength?: number;
}

export function useChatInputSetup({ maxLength }: UseChatInputSetupOptions = {}): ChatInputContextValue {
  const { sendMessage } = useChatActions<UIMessageWithMetadata>();
  const activeStoreChatId = useChatId();
  const status = useChatStatus();
  const { accountUuid, classUuid } = useNovaContext();
  const apolloClient = useApolloClient();
  const navigation = useNovaNavigationSafe();
  const {
    additionalContext,
    chatUuid,
    userUuid,
    accountName,
    displayAccount,
    hasAnyMessages,
    isVisible,
    isStandaloneMode,
    isPublicMode,
    notificationController,
    appType,
    effectiveAppType,
    setAccountUuid,
    queuedNewChatInput,
    clearQueuedNewChatInput,
    setRetryLastRequest,
    embedContext,
    onEmbedContextSent,
  } = useChatOverlayContext();

  const analytics = useAnalytics();
  const skillsContext = useSkillsSafe();
  const [input, setInput] = useState('');
  const editorRef = useRef<HTMLTextAreaElement | HTMLDivElement>(null);
  const promptEditorRef = useRef<PromptEditorHandle>(null);
  const writtenChatUuidsRef = useRef<Set<string>>(new Set());
  const handledQueuedNewChatInputRef = useRef<QueuedNewChatInput | null>(null);
  // Tracks whether we've already fired the "first typed" event for the current message.
  // Reset after each submit so we track once per message.
  const hasTrackedFirstTypedRef = useRef(false);

  const resetHeight = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.style.height = 'auto';
      editorRef.current.style.height = '94px';
    }
  }, []);

  const trackingSetInput = useCallback(
    (value: string | ((prev: string) => string)) => {
      setInput((prev) => {
        const next = typeof value === 'function' ? value(prev) : value;
        if (!hasTrackedFirstTypedRef.current && prev === '' && next !== '') {
          const attachmentCount = contextUploads(additionalContext).length;
          analytics.track({
            event: 'Nova Chat Input First Typed',
            properties: { hasAttachments: attachmentCount > 0, attachmentCount },
          });
          hasTrackedFirstTypedRef.current = true;
        }

        return next;
      });
    },
    [additionalContext, analytics],
  );

  const handleSubmit = useCallback(
    async (
      e?: React.FormEvent<HTMLFormElement>,
      manualInput?: string,
      manualAdditionalContext?: Record<string, unknown>,
    ) => {
      e?.preventDefault();

      // The editor clears immediately after submit. Capture its identity before
      // resolving asynchronous context so the selected Lexical node still exists.
      const selectedSkillId = manualInput === undefined ? promptEditorRef.current?.getSelectedSkillId() : undefined;

      const { messageMetadata, ...payloadAdditionalContext } = manualAdditionalContext ?? additionalContext ?? {};
      const attachmentUploads = contextUploads(payloadAdditionalContext);
      const normalizedAdditionalContext = {
        ...payloadAdditionalContext,
        ...(attachmentUploads.length > 0 ? { attachmentUploads } : {}),
      };

      const bodyData = await buildNovaRequestContext({
        appType,
        accountUuid,
        additionalContext: normalizedAdditionalContext,
        classUuid,
      });
      const messageText = manualInput || input.trim();

      // Enforce the message length limit at the single chokepoint all submit paths
      // funnel through (Enter key, send button, form submit, programmatic). This is
      // what actually prevents sending; the send button's disabled state and the
      // character counter are only reflections of the same limit.
      if (maxLength !== undefined && messageText.length > maxLength) {
        return;
      }

      if (messageText) {
        const submittedAt = new Date().toISOString();
        const metadata: UIMessageWithMetadata['metadata'] = {
          files: [],
          ...(messageMetadata && typeof messageMetadata === 'object' ? messageMetadata : {}),
          ...(selectedSkillId ? { selectedSkillId } : {}),
          // Provisional client stamp so the timestamp renders immediately; the persisted column wins on reload.
          createdAt: submittedAt,
        };

        if (chatUuid.startsWith('new-chat-') && !hasAnyMessages) {
          setAccountUuid(accountUuid);
        }

        const attachmentCount = attachmentUploads.length;
        const firstSkillCommandHandle = messageText.match(/^\/([\w-]+)(?:\s|$)/)?.[1];
        const submittedSkill = selectedSkillId
          ? skillsContext?.findSkill({ skillId: selectedSkillId })
          : firstSkillCommandHandle
            ? skillsContext?.findSkill({ command: firstSkillCommandHandle })
            : undefined;
        analytics.track({
          event: 'Nova Chat Message Submitted',
          properties: {
            hasAttachments: attachmentCount > 0,
            attachmentCount,
            chatUuid,
            messageLengthBucket: getLengthBucket(messageText.length),
          },
        });

        if (submittedSkill) {
          trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.SKILL_COMMAND_SUBMITTED, {
            attachmentCountBucket: getCountBucket(attachmentCount),
            category: submittedSkill.category,
            chatUuid,
            commandHandle: submittedSkill.command,
            commandId: submittedSkill.id,
            commandKind: 'skill',
            hasAttachments: attachmentCount > 0,
            messageLengthBucket: getLengthBucket(messageText.length),
            skillId: submittedSkill.id,
            skillSource: submittedSkill.source,
          });

          trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.SKILL_INVOCATION_SUBMITTED, {
            attachmentCountBucket: getCountBucket(attachmentCount),
            category: submittedSkill.category,
            chatUuid,
            commandHandle: submittedSkill.command,
            commandId: submittedSkill.id,
            commandKind: 'skill',
            hasAttachments: attachmentCount > 0,
            messageLengthBucket: getLengthBucket(messageText.length),
            skillId: submittedSkill.id,
            skillSource: submittedSkill.source,
          });
        }

        const messageId = v4();
        const userMessage = {
          id: messageId,
          role: 'user' as const,
          parts: [{ type: 'text' as const, text: messageText }],
          metadata,
        };
        // Only a new chat's first message carries the host's context: the backend stores it with the chat.
        const sendsEmbedContext = Boolean(embedContext) && chatUuid.startsWith('new-chat-') && !hasAnyMessages;
        const requestOptions = { body: sendsEmbedContext ? { ...bodyData, embedContext } : bodyData };

        hasTrackedFirstTypedRef.current = false;
        setRetryLastRequest(() => sendMessage({ ...userMessage, messageId }, requestOptions));

        sendMessage(userMessage, requestOptions);
        if (sendsEmbedContext && embedContext) onEmbedContextSent?.(normalizeChatUuid(chatUuid), embedContext);
        if (chatUuid.startsWith('new-chat-')) navigation?.actions.chatCreated(normalizeChatUuid(chatUuid));
        if (isPublicMode) {
          setInput('');
          resetHeight();
          return;
        }

        // Keeps the chats list polling fast until the server reports the
        // stream on the chat row (matters for brand-new chats).
        notificationController.noteLocalSend();

        const currentChats = apolloClient.readQuery({
          query: GET_CHATS_BY_FILTERS,
          variables: {
            userUuid,
            appType: effectiveAppType,
            statusFilter: ACTIVE_CHAT_STATUS,
          },
        });
        const chatUuidWithoutPrefix = normalizeChatUuid(chatUuid);

        if (chatUuid.startsWith('new-chat-')) {
          // Check if we've already written this chat to the cache to prevent duplicates
          if (!writtenChatUuidsRef.current.has(chatUuid)) {
            const chatAlreadyExists = currentChats?.chatsList?.some(
              (chat) => normalizeChatUuid(chat.uuid ?? '') === chatUuidWithoutPrefix,
            );

            if (!chatAlreadyExists && !isPublicMode) {
              const optimisticTimestamp = submittedAt;
              const optimisticAccountName = getOptimisticChatAccountName({
                displayAccountName: displayAccount?.name,
                accountName,
              });
              const optimisticChat = buildOptimisticChat(
                {
                  uuid: chatUuidWithoutPrefix,
                  userUuid,
                  accountUuid: accountUuid ?? null,
                  account: accountUuid ? { uuid: accountUuid, name: optimisticAccountName } : null,
                  appType: appType ?? AppTypeEnum.NAVIGATOR,
                  status: ACTIVE_CHAT_STATUS,
                  title: 'New Chat',
                  summary: 'New Chat',
                  createdAt: optimisticTimestamp,
                  updatedAt: optimisticTimestamp,
                  deletedAt: null,
                  activeStreamId: null,
                  lastMessageAt: optimisticTimestamp,
                  hasUnreadMessages: false,
                  pinnedAt: null,
                },
                {},
              );

              // Optimistic entries are written under the real (unprefixed) chat
              // uuid so returning to this chat before the backend has persisted
              // it takes the normal existing-chat path: the cached snapshot
              // hydrates the just-sent message and the resume probe can attach
              // once the server reports a stream.
              apolloClient.writeQuery({
                query: GET_CHATS_BY_FILTERS,
                variables: {
                  userUuid,
                  appType: effectiveAppType,
                  statusFilter: ACTIVE_CHAT_STATUS,
                },
                data: {
                  // The server owns stream liveness; the in-progress indicator
                  // for the active chat derives from the local chat status.
                  chatsList: [optimisticChat, ...(currentChats?.chatsList ?? [])],
                },
              });

              // Mark this chat UUID as written to prevent duplicates
              writtenChatUuidsRef.current.add(chatUuid);

              apolloClient.writeQuery({
                query: GET_CHAT_WITH_MESSAGES,
                variables: { uuid: chatUuidWithoutPrefix },
                data: {
                  getChat: {
                    userUuid,
                    accountUuid: accountUuid ?? null,
                    appType: appType ?? AppTypeEnum.NAVIGATOR,
                    account: {
                      uuid: accountUuid ?? null,
                      name: optimisticAccountName,
                    },
                    title: 'New Chat',
                    summary: 'New Chat',
                    status: ACTIVE_CHAT_STATUS,
                    uuid: chatUuidWithoutPrefix,
                    messages: [
                      {
                        uuid: messageId,
                        chatUuid: chatUuidWithoutPrefix,
                        reaction: null,
                        role: 'user',
                        files: [],
                        parts: userMessage.parts,
                        metadata,
                        createdAt: optimisticTimestamp,
                      },
                    ],
                    activeStreamId: null,
                    hasUnreadMessages: false,
                    pinnedAt: null,
                    createdAt: optimisticTimestamp,
                    updatedAt: optimisticTimestamp,
                    deletedAt: null,
                    embedContext: sendsEmbedContext && embedContext ? embedContext : null,
                  },
                },
              });
            }
          }
        }

        setInput('');
        resetHeight();
      }
    },
    [
      input,
      maxLength,
      additionalContext,
      sendMessage,
      accountUuid,
      classUuid,
      resetHeight,
      chatUuid,
      userUuid,
      accountName,
      displayAccount?.name,
      hasAnyMessages,
      apolloClient,
      isVisible,
      isStandaloneMode,
      notificationController,
      appType,
      effectiveAppType,
      isPublicMode,
      setAccountUuid,
      setRetryLastRequest,
      analytics,
      skillsContext,
      embedContext,
      onEmbedContextSent,
    ],
  );

  useEffect(() => {
    if (!queuedNewChatInput || handledQueuedNewChatInputRef.current === queuedNewChatInput) {
      return;
    }

    if (queuedNewChatInput.submitImmediately && activeStoreChatId !== chatUuid) {
      return;
    }

    handledQueuedNewChatInputRef.current = queuedNewChatInput;
    hasTrackedFirstTypedRef.current = false;
    clearQueuedNewChatInput();

    if (queuedNewChatInput.submitImmediately) {
      queueMicrotask(() => {
        void handleSubmit(undefined, queuedNewChatInput.inputText, queuedNewChatInput.additionalContext ?? {});
      });
      return;
    }

    setInput(queuedNewChatInput.inputText);

    window.setTimeout(() => {
      const editor = editorRef.current;
      if (!editor) {
        return;
      }

      editor.focus();
      editor.style.height = 'auto';
      editor.style.height = `${editor.scrollHeight + 2}px`;

      if (editor instanceof HTMLTextAreaElement) {
        editor.setSelectionRange(queuedNewChatInput.inputText.length, queuedNewChatInput.inputText.length);
        return;
      }

      if (editor instanceof HTMLDivElement) {
        const range = document.createRange();
        range.selectNodeContents(editor);
        range.collapse(false);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }, 0);
  }, [activeStoreChatId, chatUuid, clearQueuedNewChatInput, handleSubmit, queuedNewChatInput]);

  const { isAtBottom, scrollToBottom } = useScrollToBottom();

  return useMemo(
    () => ({
      input,
      setInput: trackingSetInput,
      editorRef: editorRef as React.RefObject<HTMLTextAreaElement | HTMLDivElement>,
      promptEditorRef,
      handleSubmit,
      status,
      isAtBottom,
      scrollToBottom,
      isPublicMode,
      maxLength,
    }),
    [
      input,
      editorRef,
      promptEditorRef,
      handleSubmit,
      status,
      isAtBottom,
      scrollToBottom,
      isPublicMode,
      maxLength,
      trackingSetInput,
    ],
  );
}
